import React, { useState, useEffect, useCallback, useRef,useMemo } from 'react';
import { Alert, Snackbar, Typography, Tooltip, InputAdornment, Stack, Container, useTheme, IconButton, Box, Button, TextField } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import MoreTimeOutlinedIcon from '@mui/icons-material/MoreTimeOutlined'; // Keep if used
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CoPresentOutlinedIcon from '@mui/icons-material/CoPresentOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import Header from "../../components/Header";
import { fetchUsers, reset_password } from "../../actions/auth"; // Ensure fetchUsers is updated
import FormDialog from "../form";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from "../../components/LoadingOverlay";

// Debounce helper function
function debounce(func, wait) { /* ... */
    let timeout;
    return function executedFunction(...args) {
      const later = () => { clearTimeout(timeout); func(...args); };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
}

const Team = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- State ---
  // ... (State variables remain the same) ...
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState({ severity: "info", message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [totalMemberCount, setTotalMemberCount] = useState(0);

  console.log('selectedUsers',selectedUsers)
  // --- Add this useEffect hook ---
  useEffect(() => {
    console.log('selectedUsers:', selectedUsers);
  }, [selectedUsers]); // Dependency array ensures this runs only when selectedUsers changes
  // --- End of added useEffect ---
  // --- Redux State ---
  const currentUser = useSelector(state => state.auth.user);
  const currentUserRole = currentUser?.group_names?.[0] || '';

  // --- Debounce Handler ---
  const debouncedSearchHandler = useRef( /* ... */
    debounce((value) => {
      setDebouncedSearchTerm(value);
      setPage(0);
    }, 500)
  ).current;

  const handleSearchChange = useCallback((event) => { /* ... */
      const newSearchText = event.target.value;
      setSearchText(newSearchText);
      debouncedSearchHandler(newSearchText);
  }, [debouncedSearchHandler]);

  // --- Data Fetching ---
  const loadUsers = useCallback(async () => { /* ... (same as before) ... */
      setIsLoading(true);
      try {
        const data = await fetchUsers(debouncedSearchTerm, page + 1, pageSize);
        setUsers(Array.isArray(data.results) ? data.results : []);
        setRowCount(data.count || 0);
      } catch (error) {
        console.error("Error loading users:", error);
        handleUserActionFeedback(false, `Error fetching users: ${error.message || 'Check console'}.`);
        setUsers([]); setRowCount(0);
      } finally { setIsLoading(false); }
  }, [debouncedSearchTerm, page, pageSize]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Update total member count based on rowCount
  useEffect(() => { setTotalMemberCount(rowCount); }, [rowCount]);

  // --- Event Handlers ---
  const handleCloseSnackbar = (event, reason) => { /* ... */
      if (reason === 'clickaway') return;
      setSnackbarOpen(false);
  };
  const handleRowClick = useCallback((params, event) => { /* ... (same as before) ... */
      if (event.target.closest('.MuiCheckbox-root')) { return; }
      navigate(`/students/${params.id}/exams`);
  }, [navigate]);
  const handleUserActionFeedback = (success, message) => { /* ... */
      setSnackbarContent({ severity: success ? "success" : "error", message });
      setSnackbarOpen(true);
  };
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = (refresh = false) => { /* ... */
      setOpenDialog(false);
      if (refresh) { loadUsers(); }
  };
  const handleResetPassword = useCallback(() => { /* ... (same as before) ... */
      setIsResettingPassword(true);
      const selectedUserDetails = users.filter(user => selectedUsers.includes(user.id));
      const emailsToReset = selectedUserDetails.map(user => user.email);
      let completedRequests = 0; let successCount = 0; let errorOccurred = false;
      const totalRequests = emailsToReset.length;
      if (totalRequests === 0) { setIsResettingPassword(false); handleUserActionFeedback(false, "No users selected."); return; }
      emailsToReset.forEach(email => {
        dispatch(reset_password(email))
          .then(() => { successCount++; })
          .catch(() => { errorOccurred = true; })
          .finally(() => {
              completedRequests++;
              if (completedRequests === totalRequests) {
                  if (errorOccurred) { handleUserActionFeedback(false, `Password reset attempted for ${totalRequests} with ${successCount} successes and errors.`); }
                  else { handleUserActionFeedback(true, `Password reset successful for ${successCount} users.`); setSelectedUsers([]); }
                   setIsResettingPassword(false);
              }
          });
      });
  }, [selectedUsers, dispatch, users]);

  // --- Grid Columns Definition ---
  const columns = useMemo(() => [
    // ... (Column definitions remain the same, including the updated Role renderCell) ...
     { field: "id", headerName: "ID", width: 70, sortable: false },
     {
        field: "first_name",
        headerName: "First Name",
        flex: 1, minWidth: 150, sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
            <> {row.first_name || ''} </>
            {(row.time_multiplier && parseFloat(row.time_multiplier) !== 1.0) && (
              <Tooltip title={`Time Accommodation: ${row.time_multiplier}x`} placement="right">
                <IconButton size="small" sx={{ p: 0.2, ml: 0.5 }} aria-label="time accommodation info">
                  <MoreTimeOutlinedIcon color="info" sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
      { field: "last_name", headerName: "Last Name", flex: 1, minWidth: 150, sortable: false },
      { field: "email", headerName: "Email", flex: 1, minWidth: 200, sortable: false },
      {
        field: "groups",
        headerName: "Access Level",
        flex: 1, minWidth: 150, sortable: false, align: 'center', headerAlign: 'center',
        renderCell: ({ row }) => {
            const role = row?.group_names?.[0]?.toUpperCase() || 'UNKNOWN';
            let IconComponent;
            let bgColor = colors.grey[700];
            switch (role) {
                case 'ADMIN': IconComponent = AdminPanelSettingsOutlinedIcon; break;
                case 'TEACHER': IconComponent = CoPresentOutlinedIcon; break;
                case 'STUDENT': IconComponent = SchoolOutlinedIcon; bgColor = colors.greenAccent[600]; break;
                default: IconComponent = null;
            }
            const textColor = theme.palette.getContrastText(bgColor);
            return (
              <Box sx={{ width: "80%", m: "0 auto", p: "5px", display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: bgColor, borderRadius: "4px", }}>
                {IconComponent && <IconComponent sx={{ color: textColor, fontSize: '1rem', mr: '5px' }} />}
                <Typography variant="body2" sx={{ color: textColor }}> {role} </Typography>
              </Box>
            );
        },
      },
  ], [colors, theme.palette, navigate]); // Added theme dependency

  // --- Render Logic ---
  return (
    <Container sx={{ margin: '20px auto', maxWidth: 'lg' }}>
      {/* Use totalMemberCount which is derived from rowCount */}
      <Header title="MANAGE TEAM" subtitle={`Total Members Found: ${totalMemberCount}`} page="manageTeam" />

      {/* Action Buttons */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          {/* ... Buttons ... */}
          <Button variant="contained" color="primary" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenDialog}> Add User </Button>
          <Button variant="outlined" color="secondary" startIcon={isResettingPassword ? null : <RestartAltIcon />} onClick={handleResetPassword} disabled={!selectedUsers.length || isResettingPassword} sx={{ position: 'relative' }}>
            {isResettingPassword ? "Resetting..." : "Reset Password"}
            {isResettingPassword && <LoadingOverlay size={24} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }} />}
          </Button>
      </Stack>

      {/* Search Field */}
      <TextField
        fullWidth
        label="Search by Name, Email, Role (e.g., Student)"
        variant="outlined"
        value={searchText}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
        InputProps={{
          endAdornment: ( <InputAdornment position="end"> <IconButton edge="end" aria-label="search"> <SearchIcon /> </IconButton> </InputAdornment> ),
        }}
      />

      {/* DataGrid Wrapper */}
      <Box sx={{ height: '70vh', width: '100%' }}>
        {/* --- >> UPDATED DataGrid Props << --- */}
        <DataGrid
          rows={users}
          columns={columns}
          // --- Server-side Pagination ---
          pagination paginationMode="server"
          rowCount={rowCount}
          page={page}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => { setPageSize(newPageSize); setPage(0); }}
          // --- Server-side Filtering ---
          filterMode="server"
          // --- Selection ---
          checkboxSelection
          onRowSelectionModelChange={(newSelection) => {
            setSelectedUsers(newSelection); // Update selected users
          }}          selectionModel={selectedUsers}
          onRowClick={handleRowClick}
          disableSelectionOnClick
          keepNonExistentRowsSelected // <-- Added for consistency
          // --- Loading State ---
          loading={isLoading}
          // --- Styling & Misc ---
          density="compact" // <-- Added for consistency
          getRowId={(row) => row.id} // <-- Added explicit ID getter
          sx={{
             // Merged styles
            "--DataGrid-overlayHeight": "200px", // <-- Added from ManageMyExams
            "& .MuiDataGrid-cell": { borderBottom: "none" }, // <-- Kept from original Team
            // Add other global styles from your theme if applicable
          }}
        />
         {/* --- >> END OF UPDATED DataGrid Props << --- */}
      </Box>

      {/* Dialog and Snackbar */}
      <FormDialog open={openDialog} handleClose={handleCloseDialog} onActionFeedback={handleUserActionFeedback} userRole={currentUserRole} />
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarContent.severity} sx={{ width: '100%' }}>
            {snackbarContent.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Team;